import { Button, Card, Flex, Input, Space, Spin, Table, TableColumnsType, Typography } from "antd";
import { TAccountRolePrivileges, TRole } from "api/services/models/types";
import { getAllRoles } from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { ReactComponent as EditIcon } from "Icons/vuesax/edit-2.svg";
import { useCallback, useEffect, useState } from "react";
import AddOrUpdateRole from "./AddOrUpdateRole";

type TAddOrEditRoleState = {
  open: boolean;
  role?: TRole;
};
const composeColumns = (onEditRole: (role: TRole) => void): TableColumnsType<TRole> => [
  {
    title: "Edit",
    width: "80px",
    render: (_, role) => (
      <EditIcon className="settings-edit-icon" onClick={() => onEditRole(role)} />
    )
  },
  {
    title: "Name",
    dataIndex: "description",
    key: "description",
    sorter: (s1, s2) => s1.description.localeCompare(s2.description),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()} role="none">
        <Input
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            onClick={() => clearFilters?.({ closeDropdown: true, confirm: true })}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => confirm({ closeDropdown: true })}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
        </Space>
      </div>
    ),
    filterDropdownProps: {
      placement: "bottom",
    },
    filterSearch: true,
    onFilter: (value, role) => role.description.toLowerCase().includes((value as string).toLowerCase())
  }
];

const ManageRoles = () => {
  const { accountId } = useTDAuthentication();
  const { refresh } = usePicklists();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TAccountRolePrivileges>();
  const [roleState, setRoleState] = useState<TAddOrEditRoleState>({ open: false });
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      getAllRoles(accountId)
        .then(setData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [accountId]);

  const addRole = useCallback(() => {
    setRoleState({ open: true });
  }, []);

  const editRole = useCallback((role: TRole) => {
    setRoleState({ open: true, role });
  }, []);

  const onAddOrEditClose = useCallback((newOrUpdatedRole?: TRole) => {
    setRoleState({ open: false });
    if (newOrUpdatedRole) {
      setData(data => {
        if (data) {
          const index = data.roles.findIndex(r => r.roleId === newOrUpdatedRole.roleId);
          if (index === -1) {
            data.roles.push(newOrUpdatedRole);
          } else {
            data.roles.splice(index, 1, newOrUpdatedRole);
          }
          refresh?.("Roles");
        }
        return data;
      });
    }
  }, []);

  if (!data) {
    return <div>No data found</div>;
  }
  return (
    data && (
      <>
        {loading && (
          <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
            <Spin tip="Loading, please wait..." />
          </Flex>
        )}
        {!loading && (
          <Card
            style={{
              padding: "16px"
            }}
            className="default-border"
            styles={{ body: { padding: "0" } }}>
            <Flex justify="space-between">
              <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
                Manage Roles
              </Typography.Title>
              <Button
                onClick={addRole}
                type="primary"
                style={{ display: "flex", margin: "16px 8px", alignItems: "center" }}>
                Add New Role
              </Button>
            </Flex>
            {roleState.open && (
              <AddOrUpdateRole
                onClose={onAddOrEditClose}
                role={roleState.role}
                allPrivileges={data.allPrivileges}
              />
            )}
            <Card className="card-style" styles={{ body: { padding: 0 } }}>
              <Table<TRole>
                columns={composeColumns(editRole)}
                dataSource={data.roles.sort((a, b) => a.description.localeCompare(b.description))}
                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: {
                    showSearch: false
                  },
                  pageSizeOptions: [5, 10, 20, 50, 100],
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || 10);
                  }
                }}
                rowKey={"roleId"}
                style={{ border: "none" }}
                className="table-style"
                components={{
                  body: {
                    cell: (props: any) => (
                      <td
                        {...props}
                        style={{
                          borderBottomWidth: "1px",
                          borderBottomColor: "#f0f0f0",
                          borderBottomStyle: "solid",
                          fontWeight: "500"
                        }}
                      />
                    )
                  }
                }}
              />
            </Card>
          </Card>
        )}
      </>
    )
  );
};

export default ManageRoles;
